import { useContext, useEffect, useId, useMemo, useRef, useState } from 'react'
import {
  ActionDependant,
  GlobalDataContext,
  TemplateContext,
  dataLocationToPath,
  evaluateTemplateValue,
  evaluateTemplateValueCollection,
  useEvaluatedAttributes,
} from '@ea-lab/reactive-json'
import {
  asArray,
  computeResult,
  flattenQuestions,
  interpolate,
  points,
} from './scoredQuizModel'

/**
 * Primitive GÉNÉRIQUE — le moteur d'un questionnaire NOTÉ à N dimensions
 * (auto-diagnostic de maturité, audit express, quiz de qualification).
 *
 * Pourquoi du JSX ici, et seulement ici : le YAML déclaratif ne sait ni tenir
 * un curseur de question, ni ADDITIONNER des points, ni mélanger un ordre de
 * réponses — et, sur un clic, reactive-json ignore les conditions d'action
 * (toutes les actions partent, la dernière gagne), donc « enregistrer la
 * réponse de CETTE question » n'a pas d'écriture déclarative possible.
 *
 * Ce composant ne rend donc QUE l'écran de questions. L'accueil et le résultat
 * restent du YAML : le moteur publie son résultat dans le magasin
 * (`resultPath`) et bascule l'écran (`screenPath`), le reste est du rendu
 * déclaratif — donc éditable en place et habillé par le design system.
 *
 * Tout le CONTENU vient de la donnée : rien de ce composant ne connaît le
 * diagnostic SCAFE. L'habillage vient d'une recette CSS (`class` sur la
 * racine), aucune couleur n'est écrite ici.
 *
 * Usage YAML :
 *   - type: ScoredQuiz
 *     dimensions: ~~.apiData.data.objectData.data.dimensions
 *     tiers: ~~.apiData.data.objectData.data.tiers
 *     resultPath: ~~.quizResult
 *     screenPath: ~~.quizScreen
 *     resultScreen: result      # écran à afficher une fois le score calculé
 *     restartScreen: intro      # écran dont le retour REMET le questionnaire à zéro
 *     classPrefix: scafe-quiz   # préfixe des classes CSS du site (obligatoire en pratique)
 *     labels:
 *       progress: "Votre progression"
 *       part: "Partie {n}/{total} — {name}"
 *       counter: "Question {n} / {total} · ~3 min"
 *       back: "← Retour"
 *       next: "Question suivante →"
 *       last: "Voir mon diagnostic →"
 *     attributes: { class: "scafe-quiz__split" }
 *
 * Forme attendue d'une dimension :
 *   { key, label, name, icon, color,
 *     adviceLow, adviceMid, adviceHigh,
 *     questions: [{ text, answers: [{ text, points }] }] }
 * Forme d'un palier : { max, name, text, watch } — bornes SUPÉRIEURES, triées.
 */

// Repère visuel d'une réponse, purement décoratif (aria-hidden). Au-delà de
// l'alphabet on retombe sur le rang : `charAt` rendrait une pastille vide.
const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
const optionMark = (i) => LETTERS.charAt(i) || String(i + 1)

export const ScoredQuiz = ({ props, path }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)
  const resolve = (v) => evaluateTemplateValue({ valueToEvaluate: v, globalDataContext, templateContext })

  const dimensions = asArray(resolve(props.dimensions))
  const tiers = asArray(resolve(props.tiers))
  // `labels` est un OBJET de chemins : `evaluateTemplateValue` rendrait l'objet
  // tel quel et les chemins s'afficheraient EN CLAIR à l'écran, sans erreur.
  // Seule la variante « collection » en résout les valeurs (sur un niveau).
  const labels = evaluateTemplateValueCollection({
    valueToEvaluate: props.labels ?? {},
    globalDataContext,
    templateContext,
  }) ?? {}
  const shuffle = resolve(props.shuffle) !== false
  const autoAdvanceDelay = Number(resolve(props.autoAdvanceDelay) ?? 320)
  const evaluatedAttrs = useEvaluatedAttributes(props.attributes ?? {})
  // L'habillage est celui du SITE : le préfixe de classes vient du YAML, aucune
  // classe de marque n'est écrite ici. Sans lui, la primitive n'est réutilisable
  // qu'avec la recette CSS du premier site qui l'a employée.
  const cls = resolve(props.classPrefix) || 'scored-quiz'

  const toPath = (dataLocation) =>
    dataLocation
      ? dataLocationToPath({ currentPath: path, dataLocation, globalDataContext, templateContext })
      : null
  const resultPath = toPath(props.resultPath)
  const screenPath = toPath(props.screenPath)
  const resultScreen = resolve(props.resultScreen) ?? 'result'
  const restartScreen = resolve(props.restartScreen) ?? 'intro'
  // `hide` n'est PAS un démontage : `Hide` de reactive-json rend `null` mais le
  // composant reste monté, donc son état survit à un changement d'écran. Sans
  // relire l'écran courant, « Refaire le diagnostic » réafficherait la dernière
  // question déjà répondue.
  const screen = screenPath ? resolve(props.screenPath) : undefined

  // La signature du contenu : elle seule redémarre le questionnaire. En mode
  // édition, une question retouchée doit repartir à zéro (les points d'une
  // réponse supprimée ne veulent plus rien dire) ; un simple re-rendu, non.
  const signature = useMemo(() => JSON.stringify(dimensions), [dimensions])
  const flat = useMemo(
    () => flattenQuestions(dimensions, { shuffle }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [signature, shuffle],
  )

  const total = flat.length
  const [index, setIndex] = useState(0)
  const [answers, setAnswers] = useState({})
  const [picks, setPicks] = useState({})
  const timer = useRef(null)
  const headingRef = useRef(null)
  const mounted = useRef(false)
  const fromPointer = useRef(false)
  const uid = useId()

  useEffect(() => {
    setIndex(0)
    setAnswers({})
    setPicks({})
  }, [signature])

  // Retour à l'écran de départ = questionnaire remis à zéro.
  useEffect(() => {
    if (screen !== restartScreen) return
    clearTimeout(timer.current)
    setIndex(0)
    setAnswers({})
    setPicks({})
  }, [screen, restartScreen])

  useEffect(() => () => clearTimeout(timer.current), [])

  // Le focus suit la question : sans cela, l'avance automatique laisse le focus
  // sur une réponse démontée et le clavier repart du début du document.
  useEffect(() => {
    if (!mounted.current) { mounted.current = true; return }
    headingRef.current?.focus()
  }, [index])

  if (total === 0) return null

  const current = flat[Math.min(index, total - 1)] ?? flat[0]
  const dimension = dimensions[current.dimIndex] ?? {}
  const isLast = index === total - 1
  const answered = answers[index] !== undefined

  const finish = (finalAnswers) => {
    clearTimeout(timer.current)
    const result = computeResult(dimensions, tiers, flat, finalAnswers)
    if (resultPath) globalDataContext.updateData(result, resultPath)
    if (screenPath) globalDataContext.updateData(resultScreen, screenPath)
  }

  const pick = (optionIndex) => {
    const next = { ...answers, [index]: points(current.answers[optionIndex]) }
    setAnswers(next)
    setPicks({ ...picks, [index]: optionIndex })
    clearTimeout(timer.current)
    // L'avance automatique n'a lieu QU'AU POINTEUR. Dans un groupe de boutons
    // radio, les flèches du clavier déplacent le focus *et* sélectionnent :
    // avancer sur ce `change` remplacerait la question à chaque flèche, sans
    // laisser parcourir les réponses (WCAG 3.2.2, changement de contexte).
    const advance = fromPointer.current
    fromPointer.current = false
    if (advance && !isLast && autoAdvanceDelay > 0) {
      timer.current = setTimeout(() => setIndex((i) => Math.min(i + 1, total - 1)), autoAdvanceDelay)
    }
  }

  const goNext = () => {
    if (!answered) return
    clearTimeout(timer.current)
    if (isLast) finish(answers)
    else setIndex((i) => Math.min(i + 1, total - 1))
  }

  const goBack = () => {
    clearTimeout(timer.current)
    setIndex((i) => Math.max(i - 1, 0))
  }

  const headingId = `${uid}-question`

  return (
    <ActionDependant {...props}>
      <div {...evaluatedAttrs}>
        <div className={`${cls}__left`}>
          <p className={`${cls}__label ${cls}__label--light`}>{labels.progress ?? ''}</p>
          <ol className={`${cls}__rail`}>
            {dimensions.map((dim, i) => {
              const state = i === current.dimIndex ? 'current' : i < current.dimIndex ? 'done' : 'todo'
              return (
                <li
                  key={dim.key ?? i}
                  className={`${cls}__step`}
                  data-state={state}
                  aria-current={state === 'current' ? 'step' : undefined}
                >
                  <span className={`${cls}__step-n`} aria-hidden="true">{i + 1}</span>
                  <span className={`${cls}__step-t`}>{dim.label ?? dim.name ?? ''}</span>
                  <span className={`${cls}__step-c`}>
                    {state === 'current' ? `${current.qIndex + 1}/${current.qCount}` : ''}
                  </span>
                </li>
              )
            })}
          </ol>
          <p className={`${cls}__counter`}>
            {interpolate(labels.counter, { n: index + 1, total })}
          </p>
        </div>

        <div className={`${cls}__right ${cls}__right--white`}>
          <p className={`${cls}__eyebrow ${cls}__eyebrow--dark`}>
            <i aria-hidden="true" />
            {interpolate(labels.part, {
              n: current.dimIndex + 1,
              total: dimensions.length,
              name: dimension.name ?? dimension.label ?? '',
            })}
          </p>
          <h2 className={`${cls}__q`} id={headingId} ref={headingRef} tabIndex={-1}>
            {current.text}
          </h2>
          <div className={`${cls}__opts`} role="radiogroup" aria-labelledby={headingId}>
            {current.answers.map((answer, i) => {
              const id = `${uid}-${index}-${i}`
              return (
                <label className={`${cls}__opt`} key={id} htmlFor={id} onPointerDown={() => { fromPointer.current = true }}>
                  <input
                    type="radio"
                    id={id}
                    name={`${uid}-${index}`}
                    className={`${cls}__radio`}
                    checked={picks[index] === i}
                    onChange={() => pick(i)}
                  />
                  <span className={`${cls}__opt-k`} aria-hidden="true">{optionMark(i)}</span>
                  <span className={`${cls}__opt-s`}>{answer.text ?? ''}</span>
                </label>
              )
            })}
          </div>
          <div className={`${cls}__nav`}>
            {index > 0 && (
              <button type="button" className={`${cls}__back`} onClick={goBack}>
                {labels.back ?? ''}
              </button>
            )}
            <button type="button" className={`${cls}__next`} onClick={goNext} disabled={!answered}>
              {isLast ? labels.last ?? '' : labels.next ?? ''}
            </button>
          </div>
        </div>
      </div>
    </ActionDependant>
  )
}
